import { Link, router } from '@inertiajs/react'
import SystemAdminLayout from '@/layouts/SystemAdminLayout'

interface Props {
  user: {
    id: number
    email_address: string
    email_confirmed: boolean
    system_admin: boolean
    country: string | null
    job_title: string | null
    referral_source: string | null
    created_at: string
    memberships: {
      account_id: number
      account_name: string
      roles: string[]
      active: boolean
    }[]
  }
}

export default function Show({ user }: Props) {
  const impersonate = () => {
    if (!window.confirm(`Impersonate ${user.email_address}? You'll see the app exactly as they do. Every impersonation is logged.`)) return
    router.post('/system_admin/impersonation', { user_id: user.id })
  }

  const resendConfirmation = () => {
    router.post(`/system_admin/users/${user.id}/resend_confirmation`)
  }

  return (
    <SystemAdminLayout>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{user.email_address}</h1>
          <p className="text-sm text-base-content/60">Signed up {new Date(user.created_at).toLocaleDateString()}</p>
        </div>
        <Link href="/system_admin/users" className="btn btn-ghost btn-sm">
          <span className="iconify lucide--arrow-left size-4" /> All users
        </Link>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-6">
          <div className="card bg-base-100">
            <div className="card-body space-y-2">
              <h2 className="card-title text-base">Profile</h2>
              <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
                <dt className="text-base-content/60">Email</dt>
                <dd>
                  {user.email_address}{' '}
                  {user.email_confirmed
                    ? <span className="badge badge-soft badge-success badge-sm">confirmed</span>
                    : <span className="badge badge-soft badge-warning badge-sm">unconfirmed</span>}
                  {user.system_admin && <span className="badge badge-soft badge-warning badge-sm ms-1">staff</span>}
                </dd>
                <dt className="text-base-content/60">Country</dt><dd>{user.country ?? '—'}</dd>
                <dt className="text-base-content/60">Job title</dt><dd>{user.job_title ?? '—'}</dd>
                <dt className="text-base-content/60">Heard via</dt><dd>{user.referral_source ?? '—'}</dd>
              </dl>
            </div>
          </div>

          <div className="card bg-base-100">
            <div className="card-body p-0">
              <h2 className="card-title text-base px-6 pt-5">Organizations</h2>
              <table className="table table-sm">
                <thead><tr><th>Organization</th><th>Role</th><th>Status</th></tr></thead>
                <tbody>
                  {user.memberships.map((m) => (
                    <tr key={m.account_id}>
                      <td>
                        <Link href={`/system_admin/accounts/${m.account_id}`} className="link link-hover">
                          {m.account_name}
                        </Link>
                      </td>
                      <td>
                        <div className="flex flex-wrap gap-1">
                          {m.roles.map((role) => (
                            <span key={role} className="badge badge-soft badge-sm">{role}</span>
                          ))}
                        </div>
                      </td>
                      <td className="text-xs">{m.active ? 'active' : <span className="text-error">inactive</span>}</td>
                    </tr>
                  ))}
                  {user.memberships.length === 0 && (
                    <tr><td colSpan={3} className="text-center text-base-content/40 py-6">No organizations.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="card bg-base-100">
            <div className="card-body space-y-2">
              <h2 className="card-title text-base">Support actions</h2>
              {!user.system_admin && (
                <button className="btn btn-warning btn-sm justify-start" onClick={impersonate}>
                  <span className="iconify lucide--venetian-mask size-4" /> Impersonate
                </button>
              )}
              {!user.email_confirmed && (
                <button className="btn btn-ghost btn-sm justify-start" onClick={resendConfirmation}>
                  <span className="iconify lucide--mail size-4" /> Resend confirmation email
                </button>
              )}
              {user.system_admin && (
                <p className="text-xs text-base-content/50">System admins can't be impersonated.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </SystemAdminLayout>
  )
}
